import React, { useState, useEffect } from 'react'; 
import { motion, AnimatePresence } from 'framer-motion';
import { FiCalendar, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import './AdminPanel.css';
import Navbar from './Navbar';

const PerformancePage = () => {
  const [dateRange, setDateRange] = useState('today');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const navigate = useNavigate();

  const [performanceData, setPerformanceData] = useState({
    totalClicks: 0,
    totalConversions: 0,
    offers: []
  });

  const getCookie = (name) => {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  };

  const checkAuth = async () => {
    try {
      const token = getCookie('offerAdminAuth');
      if (!token) {
        navigate('/adminBT/login');
        return false;
      }

      const res = await axios.post('https://offers.cashbolt.in.net/server/', {
        action: 'checkAuth',
        authToken: token
      });

      if (!res.data.status) {
        navigate('/adminBT/login');
        return false;
      }
      return true;
    } catch (error) {
      navigate('/adminBT/login');
      return false;
    }
  };

  const fetchPerformanceData = async () => {
    if (!authChecked) return;
    
    setLoading(true);
    try {
      const token = getCookie('offerAdminAuth');
      const formattedStart = startDate.toISOString().split('T')[0];
      const formattedEnd = endDate.toISOString().split('T')[0];

      const response = await axios.post('https://offers.cashbolt.in.net/server/', {
        action: 'getPerformanceData',
        authToken: token,
        startDate: formattedStart,
        endDate: formattedEnd
      });

      if (response.data.status) {
        setPerformanceData(response.data.data);
      } else {
        console.error('Failed to fetch performance data:', response.data.message);
      }
    } catch (error) {
      console.error("Error fetching performance data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const verifyAuth = async () => {
      const isAuthenticated = await checkAuth();
      setAuthChecked(isAuthenticated);
    };
    verifyAuth();
  }, []);

  useEffect(() => {
    if (authChecked) {
      fetchPerformanceData();
    }
  }, [authChecked, dateRange, startDate, endDate]);

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    setShowDatePicker(false);
    
    const today = new Date();
    const newDates = {
      start: new Date(),
      end: new Date()
    };

    switch(range) {
      case 'today':
        break;
      case 'yesterday':
        newDates.start.setDate(today.getDate() - 1);
        newDates.end.setDate(today.getDate() - 1);
        break;
      case 'week':
        newDates.start.setDate(today.getDate() - 7);
        break;
      case 'month':
        newDates.start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      default:
        break;
    }

    setStartDate(newDates.start);
    setEndDate(newDates.end);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const calculateCR = (clicks, conversions) => {
    return clicks > 0 ? ((conversions / clicks) * 100).toFixed(2) : '0.00';
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="admin-container">
        <Navbar/>
      {/* Header */}
      <header className="admin-header">
        <h1>Offer Performance</h1>
        
        <div className="date-selector-container">
          <button 
            className="date-range-btn"
            onClick={() => setShowDatePicker(!showDatePicker)}
          >
            <FiCalendar className="calendar-icon" />
            <span>
              {dateRange === 'today' && 'Today'}
              {dateRange === 'yesterday' && 'Yesterday'}
              {dateRange === 'week' && 'Last 7 Days'}
              {dateRange === 'month' && 'This Month'}
              {dateRange === 'custom' && `${formatDate(startDate)} - ${formatDate(endDate)}`}
            </span>
            {showDatePicker ? <FiChevronUp /> : <FiChevronDown />}
          </button>
          
          <AnimatePresence>
            {showDatePicker && (
              <motion.div 
                className="date-picker-dropdown"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <div 
                  className={`date-option ${dateRange === 'today' ? 'active' : ''}`}
                  onClick={() => handleDateRangeChange('today')}
                >
                  Today
                </div>
                <div 
                  className={`date-option ${dateRange === 'yesterday' ? 'active' : ''}`}
                  onClick={() => handleDateRangeChange('yesterday')}
                >
                  Yesterday
                </div>
                <div 
                  className={`date-option ${dateRange === 'week' ? 'active' : ''}`}
                  onClick={() => handleDateRangeChange('week')}
                >
                  Last 7 Days
                </div>
                <div 
                  className={`date-option ${dateRange === 'month' ? 'active' : ''}`}
                  onClick={() => handleDateRangeChange('month')}
                >
                  This Month
                </div>
                <div className="custom-date-section">
                  <div className="custom-date-label">Custom Range:</div>
                  <div className="custom-date-inputs">
                    <input
                      type="date"
                      value={startDate.toISOString().split('T')[0]}
                      onChange={(e) => {
                        setStartDate(new Date(e.target.value));
                        setDateRange('custom');
                      }}
                    />
                    <span>to</span>
                    <input
                      type="date"
                      value={endDate.toISOString().split('T')[0]}
                      onChange={(e) => {
                        setEndDate(new Date(e.target.value));
                        setDateRange('custom');
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </header>

      {/* Performance Summary Cards */}
      <div className="performance-summary">
        <motion.div 
          className="summary-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="summary-value">{performanceData.totalClicks.toLocaleString()}</div>
          <div className="summary-label">Total Clicks</div>
          <div className="summary-date">{formatDate(startDate)}{dateRange !== 'today' && dateRange !== 'yesterday' ? ` - ${formatDate(endDate)}` : ''}</div>
        </motion.div>
        
        <motion.div 
          className="summary-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="summary-value">{performanceData.totalConversions.toLocaleString()}</div>
          <div className="summary-label">Total Conversions</div>
          <div className="summary-date">{formatDate(startDate)}{dateRange !== 'today' && dateRange !== 'yesterday' ? ` - ${formatDate(endDate)}` : ''}</div>
        </motion.div>
        
        <motion.div 
          className="summary-card"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="summary-value">{calculateCR(performanceData.totalClicks, performanceData.totalConversions)}%</div>
          <div className="summary-label">Conversion Rate</div>
          <div className="summary-date">{formatDate(startDate)}{dateRange !== 'today' && dateRange !== 'yesterday' ? ` - ${formatDate(endDate)}` : ''}</div>
        </motion.div>
      </div>

      {/* Offers Performance Table */}
      <div className="offers-table-container">
        <table className="offers-table">
          <thead>
            <tr>
              <th>Logo</th>
              <th>Name</th>
              <th>Clicks</th>
              <th>Conversions</th>
              <th>CR Rate</th>
            </tr>
          </thead>
          <tbody>
            {performanceData.offers.map((offer, index) => (
              <motion.tr 
                key={offer.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <td>
                  <div className="offer-logo" style={{ backgroundColor: `var(--primary)` }}>
                    {offer.logo}
                  </div>
                </td>
                <td>{offer.name}</td>
                <td>{offer.clicks.toLocaleString()}</td>
                <td>{offer.conversions.toLocaleString()}</td>
                <td>{calculateCR(offer.clicks, offer.conversions)}%</td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PerformancePage;